import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, Edit, Pause, Play, Trash2, Copy, ImageOff } from 'lucide-react';
import { getMyListings, createListing, updateListing, deleteListing, initiateBoostListing, getActiveBoosts, getSellerProfile } from '../services/api';
import { ListingModal, ListingFormData } from '../components/ListingModal';

interface MyListing {
  id: string;
  title: string;
  condition: string;
  price_lkr: number;
  city: string | null;
  status: string; // "active", "draft", "paused", "sold", "removed"
  photos: string[];
  stock_quantity: number;
  created_at: string;
  author?: string;
  category?: string;
  description?: string;
  paper_type?: string | null;
  discount_pct?: number;
  original_price_lkr?: number;
  is_academic?: boolean;
  is_weekend_drop?: boolean;
  weekend_drop_price?: number;
  flip_test_video_url?: string;
  is_mystery_box?: boolean;
  is_boosted?: boolean;
  boost_expires_at?: string;
  pin_potha_eligible?: boolean;
  is_bundle?: boolean;
  bundle_book_count?: number;
}

const conditionMap: Record<string, string> = {
  new: 'New',
  like_new: 'Like New',
  good: 'Good',
  fair: 'Fair',
};

const Catalogue: React.FC = () => {
  const [listings, setListings] = useState<MyListing[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [searchQ, setSearchQ] = useState('');
  const [statusTab, setStatusTab] = useState('all');
  const [conditionFilter, setConditionFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all'); // Visual only
  
  const [page, setPage] = useState(1);
  const itemsPerPage = 20;

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkPrice, setBulkPrice] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState<ListingFormData | null>(null);

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  // SP-3 Boost states
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [activeBoosts, setActiveBoosts] = useState<{ id: string; title: string; boost_expires_at: string }[]>([]);
  const [showMaxBoostModal, setShowMaxBoostModal] = useState(false);
  const [showPayRedirectModal, setShowPayRedirectModal] = useState(false);
  const [isInitiatingBoost, setIsInitiatingBoost] = useState(false);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchListings = async () => {
    setLoading(true);
    try {
      const data = await getMyListings();
      // Filter out deleted/removed
      setListings(data.filter((l: MyListing) => l.status !== 'removed'));
    } catch {
      showToast('Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveBoosts = async () => {
    try {
      const data = await getActiveBoosts();
      setActiveBoosts(data || []);
    } catch (err) {
      console.error('Failed to load active boosts', err);
    }
  };

  const fetchSellerProfile = async () => {
    try {
      const profileData = await getSellerProfile();
      console.log('subscription_tier from API:', profileData?.subscription_tier);
      if (profileData && profileData.subscription_tier) {
        setSubscriptionTier(profileData.subscription_tier);
      } else {
        setSubscriptionTier(null);
      }
    } catch (err) {
      console.error('Failed to load seller profile', err);
    }
  };

  useEffect(() => {
    fetchListings();
    fetchActiveBoosts();
    fetchSellerProfile();
  }, []);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const getBoostPrice = (tier: string | null | undefined): number => {
    if (!tier) return 99;
    const t = tier.toLowerCase().trim();
    if (t === 'pro') return 89;
    if (t.includes('scholar')) return 79;
    return 99; // free / anything else
  };

  const formatBoostExpiryDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()} ${date.getFullYear()}`;
  };

  const handleInitiateBoost = async (listingId: string) => {
    if (activeBoosts.length >= 3) {
      setShowMaxBoostModal(true);
      return;
    }
    setIsInitiatingBoost(true);
    try {
      const res = await initiateBoostListing(listingId);
      if (res && res.payment_url) {
        window.open(res.payment_url, '_blank');
        setShowPayRedirectModal(true);
      } else {
        showToast('Failed to initiate payment');
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Failed to initiate boost';
      showToast(detail);
    } finally {
      setIsInitiatingBoost(false);
    }
  };

  // ── Filtering ─────────────────────────────────────────────────────────────
  const filteredListings = useMemo(() => {
    let result = listings;
    
    // Status
    if (statusTab !== 'all') {
      result = result.filter(l => l.status === statusTab);
    }
    
    // Condition
    if (conditionFilter !== 'all') {
      result = result.filter(l => l.condition === conditionFilter);
    }
    
    // Search
    if (searchQ) {
      const q = searchQ.toLowerCase();
      result = result.filter(l => l.title.toLowerCase().includes(q));
    }
    
    return result;
  }, [listings, statusTab, conditionFilter, searchQ]);

  const visibleListings = useMemo(() => {
    return filteredListings.slice(0, page * itemsPerPage);
  }, [filteredListings, page]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(visibleListings.map(l => l.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectRow = (id: string, checked: boolean) => {
    const next = new Set(selectedIds);
    if (checked) next.add(id);
    else next.delete(id);
    setSelectedIds(next);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this listing?")) return;
    try {
      await deleteListing(id);
      showToast("Listing deleted");
      setListings(p => p.filter(l => l.id !== id));
      setSelectedIds(p => { const nx = new Set(p); nx.delete(id); return nx; });
    } catch {
      showToast("Failed to delete");
    }
  };

  const handleTogglePause = async (id: string, currentStatus: string) => {
    const newStatus = currentStatus === 'paused' ? 'active' : 'paused';
    try {
      await updateListing(id, { status: newStatus });
      showToast(newStatus === 'active' ? "Listing resumed" : "Listing paused");
      setListings(p => p.map(l => l.id === id ? { ...l, status: newStatus } : l));
    } catch {
      showToast("Failed to update status");
    }
  };

  const openAddModal = () => {
    navigate('/catalogue/create');
  };

  const openEditModal = (l: MyListing) => {
    setModalData({
      id: l.id,
      title: l.title,
      condition: l.condition,
      price_lkr: l.price_lkr,
      stock_quantity: l.stock_quantity,
      description: '', // Not in /my response
      city: l.city || '',
      photos: l.photos,
    });
    setIsModalOpen(true);
  };

  const openDuplicateModal = (l: MyListing) => {
    setModalData({
      title: l.title + ' (Copy)',
      condition: l.condition,
      price_lkr: l.price_lkr,
      stock_quantity: l.stock_quantity,
      description: '',
      city: l.city || '',
      photos: l.photos,
    });
    setIsModalOpen(true);
  };

  const handleSaveListing = async (data: ListingFormData, isDraft: boolean) => {
    const payload = {
      title: data.title,
      condition: data.condition,
      price_lkr: data.price_lkr,
      stock_quantity: data.stock_quantity,
      description: data.description,
      city: data.city,
      photos: data.photos,
      status: isDraft ? 'draft' : 'active',
    };

    if (data.id) {
      await updateListing(data.id, payload);
      showToast(isDraft ? "Saved as draft" : "Listing updated");
    } else {
      await createListing(payload);
      showToast(isDraft ? "Saved as draft" : "Listing published!");
    }
    
    setIsModalOpen(false);
    fetchListings();
  };

  // ── Bulk Actions ──────────────────────────────────────────────────────────
  const handleBulkPauseResume = async (action: 'active' | 'paused') => {
    try {
      for (let id of Array.from(selectedIds)) {
        await updateListing(id, { status: action });
      }
      showToast(action === 'active' ? "Listings resumed" : "Listings paused");
      fetchListings();
      setSelectedIds(new Set());
    } catch {
      showToast("Some updates failed");
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.size} listings?`)) return;
    try {
      for (let id of Array.from(selectedIds)) {
        await deleteListing(id);
      }
      showToast("Listings deleted");
      fetchListings();
      setSelectedIds(new Set());
    } catch {
      showToast("Some deletes failed");
    }
  };

  const handleBulkSetPrice = async () => {
    const price = parseInt(bulkPrice);
    if (!price || price <= 0) return showToast("Enter a valid price");
    try {
      for (let id of Array.from(selectedIds)) {
        await updateListing(id, { price });
      }
      showToast("Prices updated");
      fetchListings();
      setSelectedIds(new Set());
      setBulkPrice('');
    } catch {
      showToast("Some updates failed");
    }
  };

  // ── Render Helpers ────────────────────────────────────────────────────────
  const conditionBadge = (cond: string) => {
    const styles: Record<string, string> = {
      new: 'bg-green-100 text-green-700 border-green-200',
      like_new: 'bg-blue-100 text-blue-700 border-blue-200',
      good: 'bg-amber-100 text-amber-700 border-amber-200',
      fair: 'bg-gray-200 text-gray-700 border-gray-300',
    };
    return (
      <span className={`px-2 py-0.5 rounded-md text-xs font-semibold border ${styles[cond] || styles.fair}`}>
        {conditionMap[cond] || cond}
      </span>
    );
  };

  const statusBadge = (s: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-100 text-green-700 border-green-200',
      paused: 'bg-gray-200 text-gray-700 border-gray-300',
      sold: 'bg-purple-100 text-purple-700 border-purple-200',
      draft: 'bg-amber-100 text-amber-700 border-amber-200',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border capitalize ${styles[s] || styles.paused}`}>
        {s}
      </span>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto min-h-screen bg-[#F1EFE8] animate-fade-in">
      {/* Top Bar */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#085041] mb-1">Catalogue</h1>
          <p className="text-sm font-medium text-gray-500">{listings.length} total listings</p>
        </div>
        <button 
          onClick={() => navigate('/catalogue/create')}
          className="px-5 py-2.5 bg-[#085041] text-white rounded-lg font-bold shadow-sm hover:bg-teal-900 transition-colors"
        >
          + Add New Listing
        </button>
      </div>

      {/* Active Listing Count Banner */}
      <div className="mb-4 flex items-center justify-between bg-white rounded-xl px-5 py-3 border border-gray-100 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#E1F5EE] flex items-center justify-center text-[#085041] font-bold text-sm">
            📊
          </div>
          <div>
            <p className="text-sm font-bold text-[#085041]">
              {listings.filter(l => l.status === 'active').length} / 5 active listings
            </p>
            <p className="text-xs text-gray-500">Free plan — Upgrade for unlimited listings</p>
          </div>
        </div>
        <button className="text-xs bg-gradient-to-r from-[#085041] to-[#1D9E75] text-white px-4 py-1.5 rounded-lg font-bold hover:opacity-90 transition-opacity shadow-sm">
          Upgrade →
        </button>
      </div>

      {/* Boost Summary Panel (SP-3) */}
      {activeBoosts.length > 0 && (
        <div className="mb-4 bg-[#FFFDF9] rounded-xl p-5 border border-amber-100 shadow-sm animate-fade-in">
          <div className="flex items-center justify-between mb-3 border-b border-amber-100 pb-2">
            <div className="flex items-center gap-2">
              <span className="text-lg">🚀</span>
              <h3 className="font-bold text-[#085041] text-sm md:text-base">
                Active Boosts: {activeBoosts.length} / 3
              </h3>
            </div>
            <span className="text-xs bg-amber-100 text-amber-800 font-bold px-2 py-0.5 rounded-full">Active</span>
          </div>
          <div className="space-y-2">
            {activeBoosts.map(boost => (
              <div key={boost.id} className="flex justify-between items-center text-xs md:text-sm text-gray-600">
                <span className="font-bold text-teal-900">"{boost.title}"</span>
                <span className="text-gray-500 font-medium">
                  → expires {formatBoostExpiryDate(boost.boost_expires_at)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
        {/* Filters */}
        <div className="p-4 border-b border-gray-100 bg-gray-50 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex bg-white rounded-lg p-1 border border-gray-200 shadow-sm w-fit">
            {['all', 'active', 'paused', 'sold', 'draft'].map(tab => (
              <button 
                key={tab}
                onClick={() => { setStatusTab(tab); setPage(1); }}
                className={`px-4 py-1.5 text-xs font-bold rounded-md capitalize ${statusTab === tab ? 'bg-[#085041] text-white' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input 
                type="text" 
                placeholder="Search listings..." 
                value={searchQ}
                onChange={e => { setSearchQ(e.target.value); setPage(1); }}
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-[#085041] outline-none"
              />
            </div>
            
            <select 
              value={conditionFilter} 
              onChange={e => { setConditionFilter(e.target.value); setPage(1); }}
              className="w-full sm:w-auto px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 bg-white outline-none"
            >
              <option value="all">Condition: All</option>
              <option value="new">New</option>
              <option value="like_new">Like New</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
            </select>

            <select 
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              className="w-full sm:w-auto px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 bg-white outline-none"
            >
              <option value="all">Category: All</option>
              <option value="fiction">Fiction</option>
              <option value="non_fiction">Non-Fiction</option>
              <option value="educational">Educational</option>
              <option value="sinhala">Sinhala</option>
              <option value="children">Children</option>
            </select>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (
          <div className="bg-[#E1F5EE] border-b border-[#085041]/20 px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-4 animate-fade-in">
            <div className="flex items-center gap-2 text-sm font-bold text-[#085041]">
              <div className="w-5 h-5 bg-[#085041] text-white rounded flex items-center justify-center text-xs">✓</div>
              {selectedIds.size} selected
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => handleBulkPauseResume('paused')} className="text-xs bg-white text-gray-700 px-3 py-1.5 rounded shadow-sm border border-gray-200 hover:bg-gray-50 font-semibold flex items-center gap-1">
                <Pause size={14} /> Pause All
              </button>
              <button onClick={() => handleBulkPauseResume('active')} className="text-xs bg-white text-gray-700 px-3 py-1.5 rounded shadow-sm border border-gray-200 hover:bg-gray-50 font-semibold flex items-center gap-1">
                <Play size={14} /> Resume All
              </button>
              <button onClick={handleBulkDelete} className="text-xs bg-coral-50 text-coral-700 px-3 py-1.5 rounded shadow-sm border border-coral-200 hover:bg-coral-100 font-semibold flex items-center gap-1">
                <Trash2 size={14} /> Delete All
              </button>
              <div className="h-4 w-px bg-[#085041]/20 mx-1 hidden sm:block" />
              <div className="flex items-center gap-1">
                <input 
                  type="number" 
                  placeholder="Rs. Price" 
                  value={bulkPrice}
                  onChange={e => setBulkPrice(e.target.value)}
                  className="w-24 px-2 py-1.5 text-xs rounded border border-gray-300 outline-none"
                />
                <button onClick={handleBulkSetPrice} className="text-xs bg-[#085041] text-white px-3 py-1.5 rounded font-bold hover:bg-teal-900 shadow-sm">
                  Apply Price
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Skeleton & Empty States */}
        {loading ? (
          <div className="p-10 flex justify-center">
            <Loader2 className="animate-spin text-[#085041]" size={32} />
          </div>
        ) : filteredListings.length === 0 ? (
          <div className="py-20 flex flex-col items-center text-center">
            <div className="w-20 h-20 bg-cream rounded-full flex items-center justify-center text-4xl mb-4">📚</div>
            <h3 className="text-xl font-bold text-teal-900 mb-2">No listings found</h3>
            <p className="text-sm text-gray-500 mb-6 max-w-sm">
              {listings.length === 0 ? "You haven't added any books yet. Start selling today!" : "No listings match your filters."}
            </p>
            {listings.length === 0 && (
               <button onClick={openAddModal} className="px-5 py-2 bg-[#085041] text-white rounded-lg font-bold">
                 + Add Your First Book
               </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            {/* Desktop Table */}
            <table className="w-full text-left min-w-[700px]">
              <thead>
                <tr className="bg-white border-b border-gray-100">
                  <th className="py-3 px-4 w-12">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-300 text-[#085041] focus:ring-[#085041]"
                      checked={selectedIds.size === visibleListings.length && visibleListings.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </th>
                  <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Book</th>
                  <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Price</th>
                  <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Condition</th>
                  <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Stock</th>
                  <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                  <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Paper</th>
                  <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Badges</th>
                  <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Discount</th>
                </tr>
              </thead>
              <tbody>
                {visibleListings.map(l => (
                  <tr key={l.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="py-3 px-4">
                      <input 
                        type="checkbox" 
                        className="rounded text-[#085041] focus:ring-[#085041]"
                        checked={selectedIds.has(l.id)}
                        onChange={(e) => handleSelectRow(l.id, e.target.checked)}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        {l.photos[0] ? (
                          <img src={l.photos[0]} alt="cover" className="w-10 h-14 object-cover rounded shadow-sm border border-gray-200" />
                        ) : (
                          <div className="w-10 h-14 bg-gray-100 rounded border border-gray-200 flex items-center justify-center text-gray-400">
                            <ImageOff size={16} />
                          </div>
                        )}
                        <div>
                          <p className="font-bold text-teal-900 text-sm max-w-[200px] truncate">{l.title}</p>
                          <p className="text-xs text-gray-500">ID: {l.id.slice(0, 6)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-bold text-teal-700 text-sm">
                      Rs. {l.price_lkr}
                    </td>
                    <td className="py-3 px-4">
                      {conditionBadge(l.condition)}
                    </td>
                    <td className="py-3 px-4 font-semibold text-sm">
                      <span className={l.stock_quantity <= 1 ? "text-coral" : "text-gray-700"}>{l.stock_quantity}</span>
                    </td>
                    <td className="py-3 px-4">
                      {statusBadge(l.status)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEditModal(l)} className="p-1.5 text-gray-500 hover:text-teal-700 hover:bg-teal-50 rounded" title="Edit">
                          <Edit size={16} />
                        </button>
                        <button onClick={() => openDuplicateModal(l)} className="p-1.5 text-gray-500 hover:text-blue-700 hover:bg-blue-50 rounded" title="Duplicate">
                          <Copy size={16} />
                        </button>
                        <button onClick={() => handleTogglePause(l.id, l.status)} className="p-1.5 text-gray-500 hover:text-amber-700 hover:bg-amber-50 rounded" title={l.status === 'paused' ? 'Resume' : 'Pause'}>
                          {l.status === 'paused' ? <Play size={16} /> : <Pause size={16} />}
                        </button>
                        <button onClick={() => handleDelete(l.id)} className="p-1.5 text-gray-500 hover:text-coral hover:bg-coral-50 rounded" title="Delete">
                          <Trash2 size={16} />
                        </button>
                        
                        {/* SP-3 Boost Action Button */}
                        {l.status === 'active' && (
                          l.is_boosted ? (
                            <button
                              disabled
                              className="ml-2 px-3 py-1.5 text-xs font-bold text-white bg-green-600 rounded-lg cursor-not-allowed opacity-80"
                            >
                              🚀 Boosted ✓ — expires {formatBoostExpiryDate(l.boost_expires_at || '')}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleInitiateBoost(l.id)}
                              disabled={isInitiatingBoost}
                              className="ml-2 px-3 py-1.5 text-xs font-bold text-white bg-amber-500 hover:bg-amber-600 active:bg-amber-700 rounded-lg transition-colors shadow-sm disabled:opacity-50"
                            >
                              🚀 Boost — LKR {getBoostPrice(subscriptionTier)}
                            </button>
                          )
                        )}
                      </div>
                    </td>
                    {/* Paper Quality */}
                    <td className="py-3 px-4">
                      {l.paper_type === 'premium_white' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-white border border-gray-200 text-gray-700">⬜ Sudu Piti</span>
                      )}
                      {l.paper_type === 'newsprint' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-gray-800 border border-gray-700 text-white">⬛ Kalu Piti</span>
                      )}
                      {l.paper_type === 'standard' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-blue-50 border border-blue-200 text-blue-700">📖 Standard</span>
                      )}
                      {(!l.paper_type || l.paper_type === null) && (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    {/* Feature Badges */}
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {l.is_academic && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700 border border-purple-200">🎓 Kuppi</span>
                        )}
                        {l.is_weekend_drop && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-700 border border-orange-200">🛒 Maradana</span>
                        )}
                        {l.flip_test_video_url && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-sky-100 text-sky-700 border border-sky-200">🎬 Flip-Test</span>
                        )}
                        {l.is_mystery_box && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200">📦 Kilo Pettiya</span>
                        )}
                        {l.pin_potha_eligible && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-700 border border-rose-200">📌 Pin Potha</span>
                        )}
                        {l.is_boosted && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200" title={l.boost_expires_at ? `Expires: ${new Date(l.boost_expires_at).toLocaleDateString()}` : ''}>🚀 Boosted</span>
                        )}
                        {l.is_bundle && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-teal-100 text-teal-700 border border-teal-200">📚 Bundle{l.bundle_book_count ? ` (${l.bundle_book_count})` : ''}</span>
                        )}
                      </div>
                    </td>
                    {/* Discount */}
                    <td className="py-3 px-4">
                      {l.discount_pct && l.discount_pct > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200">
                          🏷️ {l.discount_pct}% OFF
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Details/Load More */}
        {visibleListings.length < filteredListings.length && (
          <div className="p-4 bg-white border-t border-gray-100 flex justify-center">
            <button 
              onClick={() => setPage(p => p + 1)}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-200 transition-colors"
            >
              Load More
            </button>
          </div>
        )}
      </div>

      <ListingModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialData={modalData}
        onSave={handleSaveListing}
        showToast={showToast}
      />

      {/* SP-3 Max Boosts Limit Modal */}
      {showMaxBoostModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100 animate-scale-up">
            <div className="text-center">
              <span className="text-4xl">🚀</span>
              <h3 className="mt-3 text-lg font-bold text-[#085041]">Limit Reached</h3>
              <p className="mt-2 text-sm text-gray-500">
                You've reached the maximum of 3 simultaneous boosts. Wait for a current boost to expire before boosting another listing.
              </p>
              <button
                onClick={() => setShowMaxBoostModal(false)}
                className="mt-6 w-full py-2.5 bg-[#085041] hover:bg-teal-900 text-white font-bold rounded-xl shadow-md transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SP-3 PayHere Redirect Info Modal */}
      {showPayRedirectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100 animate-scale-up">
            <div className="text-center">
              <span className="text-4xl">💳</span>
              <h3 className="mt-3 text-lg font-bold text-[#085041]">Payment Initiated</h3>
              <p className="mt-2 text-sm text-gray-500">
                PayHere payment opened in a new tab. Complete the payment and return here. Your listing will be boosted automatically after payment confirmation.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="mt-6 w-full py-2.5 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl shadow-md transition-colors"
              >
                ✅ Done — Refresh Page
              </button>
            </div>
          </div>
        </div>
      )}

      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-[#085041] text-white px-5 py-3 rounded-xl shadow-xl text-sm font-bold animate-slide-up">
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default Catalogue;
